import React, { useState, useEffect } from 'react';
import {
    Grid,
    Paper,
    Typography,
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Tabs,
    Tab,
    Avatar,
    Tooltip,
    CircularProgress,
    Backdrop,
    InputAdornment,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Person as PersonIcon,
    Security as SecurityIcon,
    AdminPanelSettings as AdminIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import authService from '../../services/authService';
import userService from '../../services/userService';

// Mock data - replace with API calls
const mockUsers = [
    {
        id: 1,
        name: 'Admin User',
        username: 'admin',
        email: 'admin@sunex.com',
        role: { id: 1, name: 'Administrator' },
        status: 'Active',
        type: 'employee',
        permissions: ['view-bookings', 'create-bookings', 'edit-bookings', 'delete-bookings', 'manage-users', 'view-reports']
    },
    {
        id: 2,
        name: 'Manager User',
        username: 'manager',
        email: 'manager@sunex.com',
        role: { id: 2, name: 'Manager' },
        status: 'Active',
        type: 'employee',
        permissions: ['view-bookings', 'create-bookings', 'edit-bookings', 'view-reports']
    },
    {
        id: 3,
        name: 'Operator User',
        username: 'operator',
        email: 'operator@sunex.com',
        role: { id: 3, name: 'Operator' },
        status: 'Active',
        type: 'employee',
        permissions: ['view-bookings', 'create-bookings']
    }
];

const mockRoles = [
    {
        id: 1,
        name: 'Administrator',
        slug: 'admin',
        description: 'Full system access',
        permissions: ['view-bookings', 'create-bookings', 'edit-bookings', 'delete-bookings', 'view-invoices', 'create-invoices', 'edit-invoices', 'manage-users', 'view-reports']
    },
    {
        id: 2,
        name: 'Manager',
        slug: 'manager',
        description: 'Branch manager access',
        permissions: ['view-bookings', 'create-bookings', 'edit-bookings', 'view-invoices', 'create-invoices', 'edit-invoices', 'view-reports']
    },
    {
        id: 3,
        name: 'Operator',
        slug: 'operator',
        description: 'Basic operator access',
        permissions: ['view-bookings', 'create-bookings', 'view-invoices']
    }
];

const allPermissions = [
    { id: 'view-bookings', name: 'View Bookings', description: 'Can view all bookings' },
    { id: 'create-bookings', name: 'Create Bookings', description: 'Can create new bookings' },
    { id: 'edit-bookings', name: 'Edit Bookings', description: 'Can edit existing bookings' },
    { id: 'delete-bookings', name: 'Delete Bookings', description: 'Can delete bookings' },
    { id: 'view-invoices', name: 'View Invoices', description: 'Can view all invoices' },
    { id: 'create-invoices', name: 'Create Invoices', description: 'Can create and generate invoices' },
    { id: 'edit-invoices', name: 'Edit Invoices', description: 'Can edit invoice status and details' },
    { id: 'manage-users', name: 'Manage Users', description: 'Can manage system users' },
    { id: 'view-reports', name: 'View Reports', description: 'Can view system reports' }
];

const TabPanel = ({ children, value, index, ...other }) => (
    <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
    >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
);

const UserManagement = () => {
    const [tabValue, setTabValue] = useState(0);
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openUserDialog, setOpenUserDialog] = useState(false);
    const [openRoleDialog, setOpenRoleDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
    });
    const { handleApiCallWithLoading, handleApiCall, showSuccess, showError } = useApiHandler();

    const [userForm, setUserForm] = useState({
        name: '',
        username: '',
        mobile_no: '',
        password: '',
        role_id: '',
        status: 'Active',
        type: 'employee'
    });

    const [roleForm, setRoleForm] = useState({
        name: '',
        slug: '',
        description: '',
        permissions: []
    });

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const showAlert = (message, severity = 'success') => {
        if (severity === 'success') {
            showSuccess(message);
        } else {
            showError(message);
        }
    };

    // Load data on component mount
    useEffect(() => {
        loadUsers();
        loadRoles();
        loadPermissions();
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (searchTerm !== '') {
            setPage(0);
            setPagination(prev => ({ ...prev, current_page: 1 }));
        }
        
        const timeoutId = setTimeout(() => {
            loadUsers();
        }, searchTerm ? 300 : 0); // Debounce only for search

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const loadUsers = async () => {
        setLoading(true);
        try {
            const params = {
                page: page + 1, // Convert 0-based to 1-based
                per_page: rowsPerPage
            };
            
            if (searchTerm) params.search = searchTerm;

            const response = await userService.getUsers(params);
            if (response.success) {
                setUsers(response.data);
                if (response.pagination) {
                    setPagination(response.pagination);
                }
            }
        } catch (error) {
            showAlert(error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadRoles = async () => {
        try {
            const response = await userService.getRoles();
            if (response.success) {
                setRoles(response.data);
            }
        } catch (error) {
            showAlert(error.message, 'error');
        }
    };

    const loadPermissions = async () => {
        try {
            const response = await userService.getPermissions();
            if (response.success) {
                setPermissions(response.data);
            }
        } catch (error) {
            showAlert(error.message, 'error');
        }
    };

    // User Management Functions
    const handleAddUser = () => {
        setSelectedUser(null);
        setUserForm({
            name: '',
            username: '',
            mobile_no: '',
            password: '',
            role_id: '',
            status: 'Active',
            type: 'employee'
        });
        setOpenUserDialog(true);
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setUserForm({
            name: user.name,
            username: user.username,
            mobile_no: user.mobile_no || '',
            password: '',
            role_id: user.role.id,
            status: user.status,
            type: user.type
        });
        setOpenUserDialog(true);
    };

    const handleSaveUser = async () => {
        setLoading(true);
        try {
            if (selectedUser) {
                // Update existing user
                const response = await userService.updateUser(selectedUser.id, userForm);
                if (response.success) {
                    showAlert(response.message);
                    loadUsers(); // Reload data
                }
            } else {
                // Add new user
                const response = await userService.createUser(userForm);
                if (response.success) {
                    showAlert(response.message);
                    loadUsers(); // Reload data
                }
            }
            setOpenUserDialog(false);
        } catch (error) {
            showAlert(error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            setLoading(true);
            try {
                const response = await userService.deleteUser(userId);
                if (response.success) {
                    showAlert(response.message);
                    loadUsers(); // Reload data
                }
            } catch (error) {
                showAlert(error.message, 'error');
            } finally {
                setLoading(false);
            }
        }
    };

    // Role Management Functions
    const handleAddRole = () => {
        setSelectedRole(null);
        setRoleForm({
            name: '',
            slug: '',
            description: '',
            permissions: []
        });
        setOpenRoleDialog(true);
    };

    const handleEditRole = (role) => {
        setSelectedRole(role);
        setRoleForm({
            name: role.name,
            slug: role.slug,
            description: role.description,
            permissions: role.permissions
        });
        setOpenRoleDialog(true);
    };

    const handleSaveRole = async () => {
        setLoading(true);
        try {
            if (selectedRole) {
                // Update existing role
                const response = await userService.updateRole(selectedRole.id, roleForm);
                if (response.success) {
                    showAlert(response.message);
                    loadRoles(); // Reload data
                }
            } else {
                // Add new role
                const response = await userService.createRole(roleForm);
                if (response.success) {
                    showAlert(response.message);
                    loadRoles(); // Reload data
                }
            }
            setOpenRoleDialog(false);
        } catch (error) {
            showAlert(error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRole = async (roleId) => {
        if (window.confirm('Are you sure you want to delete this role?')) {
            setLoading(true);
            try {
                const response = await userService.deleteRole(roleId);
                if (response.success) {
                    showAlert(response.message);
                    loadRoles(); // Reload data
                }
            } catch (error) {
                showAlert(error.message, 'error');
            } finally {
                setLoading(false);
            }
        }
    };

    const handlePermissionChange = (permissionId, checked) => {
        if (checked) {
            setRoleForm({
                ...roleForm,
                permissions: [...roleForm.permissions, permissionId]
            });
        } else {
            setRoleForm({
                ...roleForm,
                permissions: roleForm.permissions.filter(p => p !== permissionId)
            });
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
    };

    return (
        <DashboardLayout title="User Management" userType="employee">
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <Paper sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab icon={<PersonIcon />} label="Users" />
                        <Tab icon={<SecurityIcon />} label="Roles" />
                        <Tab icon={<AdminIcon />} label="Permissions" />
                    </Tabs>
                </Box>

                {/* Users Tab */}
                <TabPanel value={tabValue} index={0}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">System Users</Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddUser}
                        >
                            Add User
                        </Button>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            placeholder="Search users by name, username, or role..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>User</TableCell>
                                    <TableCell>Username</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Permissions</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((user) => (
                                    <TableRow key={user.id}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Avatar sx={{ mr: 2 }}>
                                                    {user.name.charAt(0)}
                                                </Avatar>
                                                {user.name}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{user.username}</TableCell>

                                        <TableCell>
                                            <Chip
                                                label={user.role.name}
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={user.status}
                                                color={user.status === 'Active' ? 'success' : 'error'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={user.permissions.join(', ')}>
                                                <Chip
                                                    label={`${user.permissions.length} permissions`}
                                                    size="small"
                                                />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => handleEditUser(user)}
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDeleteUser(user.id)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={pagination.total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelDisplayedRows={({ from, to, count }) => 
                            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                        }
                    />
                </TabPanel>

                {/* Roles Tab */}
                <TabPanel value={tabValue} index={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">User Roles</Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddRole}
                        >
                            Add Role
                        </Button>
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Role Name</TableCell>
                                    <TableCell>Slug</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Permissions</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {roles.map((role) => (
                                    <TableRow key={role.id}>
                                        <TableCell>
                                            <Typography variant="subtitle2">
                                                {role.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={role.slug} size="small" />
                                        </TableCell>
                                        <TableCell>{role.description}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {role.permissions.map((permission) => (
                                                    <Chip
                                                        key={permission}
                                                        label={permission}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => handleEditRole(role)}
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDeleteRole(role.id)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Permissions Tab */}
                <TabPanel value={tabValue} index={2}>
                    <Typography variant="h6" sx={{ mb: 2 }}>System Permissions</Typography>
                    <Grid container spacing={2}>
                        {permissions.map((permission) => (
                            <Grid item xs={12} md={6} key={permission.id}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        {permission.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {permission.description}
                                    </Typography>
                                    <Chip
                                        label={permission.id}
                                        size="small"
                                        sx={{ mt: 1 }}
                                    />
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </TabPanel>
            </Paper>

            {/* User Dialog */}
            <Dialog open={openUserDialog} onClose={() => setOpenUserDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {selectedUser ? 'Edit User' : 'Add New User'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Full Name"
                                value={userForm.name}
                                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Username"
                                value={userForm.username}
                                onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Mobile Number"
                                value={userForm.mobile_no}
                                onChange={(e) => setUserForm({ ...userForm, mobile_no: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Password"
                                type="password"
                                value={userForm.password}
                                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                                helperText={selectedUser ? "Leave blank to keep current password" : ""}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Role</InputLabel>
                                <Select
                                    value={userForm.role_id}
                                    onChange={(e) => setUserForm({ ...userForm, role_id: e.target.value })}
                                >
                                    {roles.map((role) => (
                                        <MenuItem key={role.id} value={role.id}>
                                            {role.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={userForm.status}
                                    onChange={(e) => setUserForm({ ...userForm, status: e.target.value })}
                                >
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenUserDialog(false)} startIcon={<CancelIcon />}>
                        Cancel
                    </Button>
                    <Button onClick={handleSaveUser} variant="contained" startIcon={<SaveIcon />}>
                        {selectedUser ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Role Dialog */}
            <Dialog open={openRoleDialog} onClose={() => setOpenRoleDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {selectedRole ? 'Edit Role' : 'Add New Role'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Role Name"
                                value={roleForm.name}
                                onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Slug"
                                value={roleForm.slug}
                                onChange={(e) => setRoleForm({ ...roleForm, slug: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                multiline
                                rows={2}
                                value={roleForm.description}
                                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Permissions
                            </Typography>
                            <Grid container spacing={1}>
                                {permissions.map((permission) => (
                                    <Grid item xs={12} md={6} key={permission.id}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={roleForm.permissions.includes(permission.id)}
                                                    onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="body2">
                                                        {permission.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {permission.description}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenRoleDialog(false)} startIcon={<CancelIcon />}>
                        Cancel
                    </Button>
                    <Button onClick={handleSaveRole} variant="contained" startIcon={<SaveIcon />}>
                        {selectedRole ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
};

export default UserManagement; 